import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { locationsApi, LOCATION_KINDS, previewImage, type LocationKind } from '../api/library.js';
import { SelectField } from '../components/Fields.js';
import { EmptyState } from '../components/EmptyState.js';
import { useI18n } from '../i18n/context.js';
import { LibraryCard } from '../library/LibraryCard.js';
import { ActionError } from '../library/ActionError.js';
import { useActionFeedback } from '../library/useActionFeedback.js';
import { LibraryPicker } from '../library/LibraryPicker.js';
import { SimpleEditor } from '../library/SimpleEditor.js';
import { useCreatedItemScroll } from '../library/useCreatedItemScroll.js';

/**
 * Библиотека локаций `/app/locations` (01-SPEC.md §5.10).
 * Локации показывают привязанные к ним развороты.
 */
export function LocationsPage(): JSX.Element {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookId');
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const action = useActionFeedback();
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => locationsApi.list() });
  const { scrollToCreatedItem, itemRef } = useCreatedItemScroll<HTMLLIElement>(
    locations.data?.items.map((location) => location.id) ?? [],
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['locations'] });
  };

  if (locations.isPending) return <p>{t.common.loading}</p>;
  if (locations.isError) return <p role="alert">{t.common.unexpectedError}</p>;

  const items = locations.data.items;

  return (
    <section className="mx-auto grid max-w-7xl gap-5">
      <Link
        to={bookId ? `/app/books/${encodeURIComponent(bookId)}?step=locations` : '/app'}
        className="kitup-quiet-button w-fit"
      >
        ← {t.common.back}
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">{t.locations.title}</h1>
      </div>

      <LibraryPicker
        addLabel={t.locations.add}
        createLabel={t.locations.create}
        nameLabel={t.locations.fields.name}
        options={[]}
        onAdd={() => undefined}
        onCreate={(name) =>
          locationsApi.create({ name, kind: 'room' }).then((created) => {
            setOpenId(created.id);
            scrollToCreatedItem(created.id);
            refresh();
          })
        }
      />
      <ActionError message={action.error} />

      {items.length === 0 ? (
        <EmptyState title={t.locations.empty} hint={t.locations.emptyHint} />
      ) : null}

      <ul className="grid gap-3">
        {items.map((location) => (
          <LibraryCard
            key={location.id}
            elementRef={itemRef(location.id)}
            title={location.name}
            subtitle={t.locations.kinds[location.kind]}
            previewUrl={previewImage(location)}
            usage={location.usage}
            renditions={location.renditions}
            extra={
              <p className="text-sm text-[var(--kitup-color-muted)]">
                {t.locations.spreadsTitle}: {t.library.usage.spreads(location.spreads.length)}
              </p>
            }
            open={openId === location.id}
            blocked={location.usage.activeBooks > 0}
            onToggle={() => setOpenId(openId === location.id ? null : location.id)}
            onArchive={() => action.run(locationsApi.archive(location.id).then(refresh))}
            onDelete={() => action.run(locationsApi.remove(location.id).then(refresh))}
          >
            <SimpleEditor
              entity={location}
              api={locationsApi}
              styleId={null}
              uploadKind="location_ref"
              maxPhotos={3}
              photoHint={t.library.photos.hintLocation}
              labels={{
                name: t.locations.fields.name,
                description: t.locations.fields.description,
              }}
              kindField={
                <SelectField
                  label={t.locations.fields.kind}
                  value={location.kind}
                  options={LOCATION_KINDS.map((kind) => ({
                    value: kind,
                    label: t.locations.kinds[kind],
                  }))}
                  onChange={(kind: LocationKind) =>
                    action.run(locationsApi.update(location.id, { kind }).then(refresh))
                  }
                />
              }
              onChanged={refresh}
            />
          </LibraryCard>
        ))}
      </ul>
    </section>
  );
}
